/**
 * DatePicker component for date selection
 *
 * Uses native HTML date input with custom styling for dark theme.
 * Styled with Tailwind CSS using glass morphism.
 * Supports validation states (error, success, warning).
 */

import type { DatePickerOptions, ValidationState } from '../types/form';

/**
 * Base CSS classes for the date input element
 */
const BASE_CLASSES = [
  'w-full',
  'px-3',
  'py-2',
  'text-sm',
  'text-foreground',
  'rounded-md',
  'border',
  'bg-neutral-950/60',
  'backdrop-blur-sm',
  'transition-colors',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-offset-background',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
  'cursor-pointer',
].join(' ');

/**
 * CSS classes for validation states
 */
const STATE_CLASSES: Record<ValidationState, string> = {
  default:
    'border-neutral-800 hover:border-neutral-700 focus:border-primary focus:ring-primary/20',
  error:
    'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/20',
  success:
    'border-success hover:border-success focus:border-success focus:ring-success/20',
  warning:
    'border-warning hover:border-warning focus:border-warning focus:ring-warning/20',
};

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `datepicker-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format a date to ISO date string (YYYY-MM-DD)
 */
function formatDate(date: Date): string {
  const isoString = date.toISOString();
  const parts = isoString.split('T');
  return parts[0] ?? '';
}

/**
 * Parse an ISO date string to Date object
 */
function parseDate(dateString: string): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Create a DatePicker element
 */
export function createDatePicker(options: DatePickerOptions = {}): HTMLInputElement {
  const {
    id = generateId(),
    name,
    value = '',
    placeholder,
    disabled = false,
    required = false,
    readonly = false,
    min,
    max,
    className = '',
    validationState = 'default',
    attributes = {},
    onChange,
    onFocus,
    onBlur,
  } = options;

  const input = document.createElement('input');

  // Set basic attributes
  input.type = 'date';
  input.id = id;
  input.value = value;

  if (name) input.name = name;
  if (placeholder) input.placeholder = placeholder;
  if (disabled) input.disabled = true;
  if (required) input.required = true;
  if (readonly) input.readOnly = true;

  // Date-specific attributes
  if (min !== undefined) input.min = min;
  if (max !== undefined) input.max = max;

  // Set CSS classes
  const stateClasses = STATE_CLASSES[validationState];
  input.className = `${BASE_CLASSES} ${stateClasses} ${className}`.trim();

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    input.setAttribute(key, val);
  }

  // Style adjustments for date picker in dark mode
  input.style.colorScheme = 'dark';

  // Attach event handlers
  if (onChange) {
    input.addEventListener('change', (event) => {
      onChange(input.value, event);
    });
  }

  if (onFocus) {
    input.addEventListener('focus', (event) => {
      onFocus(event as FocusEvent);
    });
  }

  if (onBlur) {
    input.addEventListener('blur', (event) => {
      onBlur(event as FocusEvent);
    });
  }

  return input;
}

/**
 * DatePicker class for object-oriented usage
 */
export class DatePicker {
  private element: HTMLInputElement;
  private options: DatePickerOptions;

  constructor(options: DatePickerOptions = {}) {
    this.options = { ...options };
    this.element = createDatePicker(this.options);
  }

  /**
   * Get the input element
   */
  getElement(): HTMLInputElement {
    return this.element;
  }

  /**
   * Get the current value as ISO date string
   */
  getValue(): string {
    return this.element.value;
  }

  /**
   * Set the value (ISO date string)
   */
  setValue(value: string): this {
    this.element.value = value;
    return this;
  }

  /**
   * Get the current value as Date object
   */
  getDate(): Date | null {
    return parseDate(this.element.value);
  }

  /**
   * Set the value from a Date object
   */
  setDate(date: Date): this {
    this.element.value = formatDate(date);
    return this;
  }

  /**
   * Set the minimum date
   */
  setMin(min: string | Date): this {
    this.element.min = typeof min === 'string' ? min : formatDate(min);
    return this;
  }

  /**
   * Set the maximum date
   */
  setMax(max: string | Date): this {
    this.element.max = typeof max === 'string' ? max : formatDate(max);
    return this;
  }

  /**
   * Set the validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;
    const stateClasses = STATE_CLASSES[state];

    // Remove all state classes
    Object.values(STATE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new state classes
    stateClasses.split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    return this;
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.element.disabled = disabled;
    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.element.disabled;
  }

  /**
   * Focus the input
   */
  focus(): this {
    this.element.focus();
    return this;
  }

  /**
   * Blur the input
   */
  blur(): this {
    this.element.blur();
    return this;
  }

  /**
   * Check validity
   */
  checkValidity(): boolean {
    return this.element.checkValidity();
  }

  /**
   * Report validity (shows browser validation message)
   */
  reportValidity(): boolean {
    return this.element.reportValidity();
  }

  /**
   * Set custom validity message
   */
  setCustomValidity(message: string): this {
    this.element.setCustomValidity(message);
    return this;
  }

  /**
   * Clear the input
   */
  clear(): this {
    this.element.value = '';
    return this;
  }

  /**
   * Set to today's date
   */
  setToday(): this {
    this.element.value = formatDate(new Date());
    return this;
  }

  /**
   * Check if the selected date is valid (within min/max range)
   */
  isValidDate(): boolean {
    if (!this.element.value) return false;

    const date = parseDate(this.element.value);
    if (!date) return false;

    const min = this.element.min ? parseDate(this.element.min) : null;
    const max = this.element.max ? parseDate(this.element.max) : null;

    if (min && date < min) return false;
    if (max && date > max) return false;

    return true;
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.removeEventListener(type, listener);
    return this;
  }
}

/**
 * Utility: Format a date for display (localized)
 */
export function formatDateForDisplay(
  dateString: string,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseDate(dateString);
  if (!date) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return date.toLocaleDateString(locale, options || defaultOptions);
}
